<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import type { AppContext, MissionInfo } from '../app';
  import { isShown } from '../core/missionState';
  import { lang, t, type MessageKey } from '../i18n';
  import { loadStatus } from '../state/loader';
  import { follow, followTarget, selectMission, stopFollowing } from '../state/stores';
  import BottomSheet from './BottomSheet.svelte';
  import EventList from './EventList.svelte';
  import FactSheet from './FactSheet.svelte';

  // The detail panel (S3-16, D15, Concept §4.3): the selected mission's story — header with the agency stripe, status
  // and Follow, the fact sheet, "At a glance" and "Deep dive", the event chronology, links, sources and the
  // data-quality note. Drafts carry a badge (review: draft, Concept §7.2).
  let { ctx, mission }: { ctx: AppContext; mission: MissionInfo } = $props();

  let tab = $state<'glance' | 'deep'>('glance');
  const d = $derived(mission.data);
  /** The production build ships no unreviewed texts (S4-18, D17): then the panel says "Text in review". */
  const inReview = $derived(d.summary[$lang] === undefined);
  const draft = $derived(!inReview && d.review[$lang] === 'draft');
  const domain = (url: string): string => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  /** Spacecraft that can be followed right now (in flight at the current date). */
  let followable = $state<string[]>([]);
  let raf = 0;
  let last = 0;
  function poll(now: number): void {
    raf = requestAnimationFrame(poll);
    if (now - last < 250) return;
    last = now;
    const ids = mission.spacecraft.filter((sc) => isShown(ctx.scene.lifeOf(sc)?.state));
    if (ids.join() !== followable.join()) followable = ids;
  }
  onMount(() => (raf = requestAnimationFrame(poll)));
  onDestroy(() => cancelAnimationFrame(raf));

  const name = (sc: string): string => ctx.targets.get(sc)?.name[$lang] ?? sc;
  const loading = $derived(mission.spacecraft.some((sc) => ['queued', 'loading'].includes($loadStatus[sc] ?? '')));

  const fetched = (iso: string | undefined, l: string): string =>
    iso
      ? new Intl.DateTimeFormat(l === 'de' ? 'de-DE' : 'en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(Date.parse(`${iso}T00:00:00Z`))
      : '';

  function close(): void {
    selectMission(null);
  }

  function onTabKey(e: KeyboardEvent): void {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    tab = tab === 'glance' ? 'deep' : 'glance';
    document.getElementById(`tab-${tab}`)?.focus();
    e.preventDefault();
    e.stopPropagation();
  }
</script>

{#snippet header()}
  <div class="header" style:--agency={mission.color}>
    <div class="stripe" aria-hidden="true"></div>
    <div class="top">
      <span class="caps-label">{mission.agencies.map((a) => a.name[$lang]).join(' · ')}</span>
      <span class="chip status-{d.status}">{$t(`status.${d.status}` as MessageKey)}</span>
      {#if draft}<span class="chip draft" title={$t('panel.draftNote')}>{$t('panel.draft')}</span>{/if}
    </div>
    <!-- name and Follow share a row; on phones the key says only "Follow" / "Stop following" (S4-15) -->
    <div class="name-row">
      <h2 id="panel-title" tabindex="-1">{d.name[$lang]}</h2>
      <div class="actions">
        {#each followable as sc (sc)}
          {#if $followTarget === sc}
            <button
              type="button"
              class="pill on"
              aria-label={$t('follow.stop', { name: name(sc) })}
              onclick={stopFollowing}
              ><span class="long">{$t('follow.stop', { name: name(sc) })}</span><span class="short"
                >{$t('follow.stopShort')}</span
              ></button
            >
          {:else}
            <button
              type="button"
              class="pill"
              aria-label={$t('follow.startLabel', { name: name(sc) })}
              onclick={() => follow(sc)}
              ><span class="long">{$t('follow.startLabel', { name: name(sc) })}</span><span class="short"
                >{$t('follow.start')}</span
              ></button
            >
          {/if}
        {/each}
        {#if loading}<span class="muted" role="status">{$t('panel.loading')}</span>{/if}
      </div>
    </div>
  </div>
{/snippet}

<BottomSheet labelledBy="panel-title" onclose={close} {header}>
  {#if d.milestone?.[$lang]}<p class="milestone">{d.milestone[$lang]}</p>{/if}
  {#if inReview}
    <p class="note in-review" data-testid="text-in-review">{$t('panel.inReview')}</p>
  {:else}
    <div class="tabs" role="tablist" aria-label={$t('panel.texts')}>
      <button
        type="button"
        role="tab"
        id="tab-glance"
        aria-selected={tab === 'glance'}
        aria-controls="tabpanel"
        tabindex={tab === 'glance' ? 0 : -1}
        onclick={() => (tab = 'glance')}
        onkeydown={onTabKey}>{$t('panel.glance')}</button
      >
      <button
        type="button"
        role="tab"
        id="tab-deep"
        aria-selected={tab === 'deep'}
        aria-controls="tabpanel"
        tabindex={tab === 'deep' ? 0 : -1}
        disabled={!d.description?.[$lang]}
        onclick={() => (tab = 'deep')}
        onkeydown={onTabKey}>{$t('panel.deep')}</button
      >
    </div>
    <div id="tabpanel" class="text" role="tabpanel" aria-labelledby="tab-{tab}">
      <p>{tab === 'deep' && d.description?.[$lang] ? d.description[$lang] : d.summary[$lang]}</p>
      {#if draft}<p class="note">{$t('panel.draftNote')}</p>{/if}
    </div>
  {/if}

  <h3 class="caps-label">{$t('panel.facts')}</h3>
  <FactSheet {ctx} {mission} />

  {#if mission.events.length}
    <h3 class="caps-label">{$t('panel.events')}</h3>
    <EventList {ctx} {mission} />
  {/if}

  {#if d.links.length}
    <h3 class="caps-label">{$t('panel.links')}</h3>
    <ul class="links">
      {#each d.links as l (l.url)}
        <li><a href={l.url} target="_blank" rel="noopener noreferrer">{l.label[$lang]}</a></li>
      {/each}
    </ul>
  {/if}

  <h3 class="caps-label">{$t('panel.quality')}</h3>
  <ul class="quality">
    {#each d.spacecraft.filter((sc) => sc.symbolicOrbit) as sc (sc.id)}
      <li>{$t('quality.symbolic')}</li>
    {/each}
    {#each d.spacecraft.filter((sc) => sc.trajectory.source === 'keyframes') as sc (sc.id)}
      <li data-testid="quality-reconstructed">
        {d.spacecraft.length > 1 ? `${sc.name ?? d.name[$lang]}: ` : ''}{$t('quality.reconstructed')}
      </li>
    {/each}
    {#each d.spacecraft.filter((sc) => sc.rejoin && sc.parent) as sc (sc.id)}
      <li data-testid="quality-rejoined">
        {sc.name ?? d.name[$lang]}: {$t('quality.rejoined', { parent: name(sc.parent!) })}
      </li>
    {/each}
    {#each d.spacecraft.filter((sc) => sc.trajectory.source === 'horizons') as sc (sc.id)}
      <li>
        {d.spacecraft.length > 1 ? `${sc.name ?? d.name[$lang]}: ` : ''}{$t(
          sc.trajectory.quality === 'predicted' ? 'quality.predicted' : 'quality.measured',
          { date: fetched(sc.trajectory.fetchedAt, $lang) },
        )}
      </li>
    {/each}
  </ul>

  <h3 class="caps-label">{$t('panel.sources')}</h3>
  <ul class="sources">
    {#each d.sources as src (src)}
      <li><a href={src} target="_blank" rel="noopener noreferrer">{domain(src)}</a></li>
    {/each}
  </ul>
</BottomSheet>

<style>
  .header {
    flex: 1;
    min-width: 0;
  }
  .stripe {
    height: 4px;
    margin: 0 0 6px;
    background: var(--agency);
  }
  .top {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .chip {
    padding: 1px 6px;
    border: 1px solid var(--rule);
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
  }
  .chip.status-active {
    border-color: var(--accent);
    color: var(--accent);
  }
  .chip.draft {
    border-color: var(--signal);
    color: var(--signal);
  }
  h2 {
    margin: 2px 0 4px;
    font-size: 1.3rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    outline: none;
  }
  .name-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 12px;
  }
  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }
  .short {
    display: none;
  }
  /* phones (S4-15): a slim peek — thinner stripe, one line of agencies, the name beside a short Follow key */
  @media (max-width: 899px) {
    .stripe {
      height: 3px;
      margin-bottom: 2px;
    }
    .top {
      flex-wrap: nowrap;
    }
    .top .caps-label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    h2 {
      margin: 0;
      font-size: 1.05rem;
    }
    /* the peek is the name and Follow; agencies, status and the draft badge come with the half sheet */
    :global(.sheet.peek) .top {
      display: none;
    }
    .pill {
      min-height: 32px;
    }
    .pill::after {
      inset: -6px 0;
    }
    .long {
      display: none;
    }
    .short {
      display: inline;
    }
  }
  .pill {
    min-height: 36px;
    padding: 0 12px;
    border: var(--rule-w) solid var(--rule);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
    position: relative;
  }
  .pill::after {
    content: '';
    position: absolute;
    inset: -4px 0;
  }
  .pill.on,
  .pill:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
  .muted {
    color: var(--fg-muted);
    font-size: 0.8rem;
  }
  .milestone {
    margin: 8px 0;
    color: var(--accent);
    font-weight: 600;
  }
  .tabs {
    display: flex;
    border-bottom: var(--rule-w) solid var(--rule);
  }
  [role='tab'] {
    min-height: var(--touch);
    padding: 0 12px;
    color: var(--fg-muted);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
    border-bottom: 3px solid transparent;
    margin-bottom: calc(-1 * var(--rule-w));
  }
  [role='tab'][aria-selected='true'] {
    color: var(--fg);
    border-bottom-color: var(--accent);
  }
  [role='tab']:disabled {
    opacity: 0.4;
  }
  .text p {
    line-height: 1.5;
  }
  .note {
    color: var(--fg-muted);
    font-size: 0.8rem;
    font-style: italic;
  }
  h3 {
    margin: 16px 0 6px;
  }
  ul {
    margin: 0;
    padding-left: 18px;
    font-size: 0.85rem;
  }
  li {
    margin: 2px 0;
  }
  a {
    color: var(--accent);
  }
  .sources {
    word-break: break-all;
  }
</style>
